import { Button, Flex, Text } from '@hubertai/hubert-component-library';
import { TextField } from '@radix-ui/themes';
import { Check, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticsEvents } from '../../analytics/events';
import { track } from '../../analytics/track';
import type { AnswerListFilter, AnswerOptionSelection, FilterQuestion } from './useAnswerFilter';
import { summarize, ticksOf, toggled, useAnswerFilterQuestions, withTicks } from './useAnswerFilter';

/** How many of the most answered questions are listed under "Suggested". */
const SUGGESTED = 5;
/** How many answers a question's preview line names before "+N". */
const PREVIEW = 3;

/**
 * The answer filter in "filter mode": it takes the place of the candidate profile. Ticks are a draft; Done applies
 * them, the candidate list next to it reloads once and the profile comes back. Counts are for the list being viewed.
 * Questions on the left (search, Suggested, All questions), the selected question's answers on the right.
 */
const AnswerFilterPanel = ({
  jobId,
  listFilter,
  selected,
  onChange,
  onClose,
}: {
  jobId: string;
  listFilter: AnswerListFilter;
  selected: AnswerOptionSelection[];
  onChange: (selected: AnswerOptionSelection[]) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [focused, setFocused] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const { questions, status, unavailable } = useAnswerFilterQuestions(jobId, true, listFilter);
  // How often jobs are too large for the filter: reported once per panel.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (!unavailable || reportedRef.current) return;
    reportedRef.current = true;
    track(AnalyticsEvents.ANSWER_FILTER_UNAVAILABLE, unavailable);
  }, [unavailable]);

  // Ticks here are a draft; the candidate list only reloads when the panel closes (Done or the filter icon).
  const setTicks = (questionId: string, optionIds: string[]) => onChange(withTicks(selected, questionId, optionIds));

  // The backend lists questions most answered first.
  const suggested = questions.slice(0, SUGGESTED);
  const rest = questions.filter((q) => !suggested.includes(q));
  const search = query.trim().toLowerCase();
  const matches = questions.filter((q) => q.title.toLowerCase().includes(search));
  // The right side is never empty: it falls back to the best suggestion.
  const shown = questions.find((q) => q.id === focused) ?? suggested[0] ?? questions[0];

  const row = (q: FilterQuestion) => (
    <QuestionRow
      key={q.id}
      question={q}
      picked={ticksOf(selected, q.id)}
      active={shown?.id === q.id}
      onSelect={() => setFocused(q.id)}
    />
  );

  return (
    <Flex
      direction="column"
      className="swiper-body @container ml-3 min-w-0 overflow-hidden! rounded-[var(--radius-4)] border border-[var(--gray-a5)] bg-white"
      data-ph-capture-attribute-region="answer_filter"
    >
      <Text as="div" size="3" weight="bold" className="border-b border-[var(--gray-a5)] px-5 py-3.5">
        {t('swiper.page.answerFilter.title')}
      </Text>

      <Flex className="min-h-0 flex-1">
        {/* Wide panel: questions and answers side by side. Narrow panel: one at a time; a question opens its answers,
            "← All questions" goes back. Decided by the panel's own width, so it also works next to a wide list. */}
        <div
          className={`${focused ? 'hidden' : 'flex'} w-full min-w-0 flex-col @[720px]:flex @[720px]:w-[300px] @[720px]:flex-none @[720px]:border-r @[720px]:border-[var(--gray-a5)] @[1000px]:w-[340px]`}
        >
          <TextField.Root
            m="3"
            mb="0"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('swiper.page.answerFilter.findQuestion')}
            aria-label={t('swiper.page.answerFilter.findQuestion')}
          >
            <TextField.Slot>
              <Search size={15} />
            </TextField.Slot>
          </TextField.Root>
          <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
            {status && <Hint>{status}</Hint>}
            {search ? (
              matches.length ? (
                <div className="pt-2">{matches.map(row)}</div>
              ) : (
                <Hint>{t('swiper.page.answerFilter.noQuestionMatch')}</Hint>
              )
            ) : (
              <>
                {suggested.length > 0 && (
                  <Heading hint={t('swiper.page.answerFilter.suggestedHint')}>
                    {t('swiper.page.answerFilter.suggested')}
                  </Heading>
                )}
                {suggested.map(row)}
                {rest.length > 0 && (
                  <Heading divider={suggested.length > 0}>{t('swiper.page.answerFilter.allQuestions')}</Heading>
                )}
                {rest.map(row)}
              </>
            )}
          </div>
        </div>

        <div className={`${focused ? 'block' : 'hidden'} min-w-0 flex-1 overflow-y-auto px-5 py-4 @[720px]:block`}>
          {focused && (
            <button
              type="button"
              onClick={() => setFocused(null)}
              className="mb-3 flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm font-medium text-[var(--violet-11)] @[720px]:hidden"
            >
              <ChevronLeft size={16} />
              {t('swiper.page.answerFilter.allQuestions')}
            </button>
          )}
          {shown && (
            <Answers
              question={shown}
              picked={ticksOf(selected, shown.id)}
              onToggle={(optionId) => setTicks(shown.id, toggled(ticksOf(selected, shown.id), optionId))}
              onClear={() => setTicks(shown.id, [])}
            />
          )}
        </div>
      </Flex>

      <Flex justify="between" align="center" className="border-t border-[var(--gray-a5)] px-5 py-3">
        <Button variant="ghost" color="gray" disabled={selected.length === 0} onClick={() => onChange([])}>
          {t('swiper.page.answerFilter.clearAll')}
        </Button>
        <Button color="violet" onClick={onClose}>
          {t('swiper.page.answerFilter.done')}
        </Button>
      </Flex>
    </Flex>
  );
};

/** A question in the list: title, then its biggest answers with counts (or what is ticked), and a chevron. */
const QuestionRow = ({
  question,
  picked,
  active,
  onSelect,
}: {
  question: FilterQuestion;
  picked: string[];
  active: boolean;
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const biggest = [...question.options].sort((a, b) => b.count - a.count);
  const [first, ...others] = biggest.slice(0, PREVIEW);
  const more = biggest.length - PREVIEW;
  return (
    <button
      type="button"
      title={question.text ?? undefined}
      aria-pressed={active}
      onClick={onSelect}
      className={`group mb-1 flex h-[52px] w-full cursor-pointer items-center gap-2 rounded-[var(--radius-3)] border-0 pr-2 pl-3 text-left ${
        active ? 'bg-[var(--violet-3)]' : 'bg-transparent hover:bg-[var(--gray-a3)]'
      }`}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Text size="2" weight={picked.length || active ? 'medium' : 'regular'} truncate>
          {question.title}
        </Text>
        {picked.length > 0 ? (
          <Text size="1" weight="medium" truncate className="text-[var(--violet-11)]">
            {t('swiper.page.answerFilter.selectedCount', { count: picked.length })}: {summarize(question, picked)}
          </Text>
        ) : (
          // The biggest answer and its count always show in full; the rest trails off if space runs out.
          <span className="flex min-w-0 items-baseline gap-3 text-xs whitespace-nowrap">
            {first && (
              <span className="flex max-w-[80%] flex-none items-baseline gap-1">
                <span className="truncate text-[var(--gray-11)]">{first.label}</span>
                <span className="flex-none text-[var(--gray-9)] tabular-nums">{first.count}</span>
              </span>
            )}
            {others.length > 0 && (
              <span className="min-w-0 truncate text-[var(--gray-9)]">
                {others.map((o, i) => (
                  <span key={o.id}>
                    {i > 0 && '   '}
                    <span className="text-[var(--gray-11)]">{o.label}</span> {o.count}
                  </span>
                ))}
                {more > 0 && `   +${more}`}
              </span>
            )}
          </span>
        )}
      </span>
      <ChevronRight
        size={16}
        className={`flex-none ${active ? 'text-[var(--violet-11)]' : 'text-[var(--gray-8)] group-hover:text-[var(--gray-11)]'}`}
      />
    </button>
  );
};

/** The selected question: its full text, then every answer with a check mark, count and share of answers. */
const Answers = ({
  question,
  picked,
  onToggle,
  onClear,
}: {
  question: FilterQuestion;
  picked: string[];
  onToggle: (optionId: string) => void;
  onClear: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Flex align="start" gap="3" pb="3">
        <div className="min-w-0 flex-1">
          <Text as="div" size="3" weight="bold">
            {question.title}
          </Text>
          {question.text && (
            <Text as="div" size="2" tone="muted" mt="1">
              {question.text}
            </Text>
          )}
        </div>
        {picked.length > 0 && (
          <Button size="1" variant="ghost" color="violet" onClick={onClear}>
            {t('swiper.page.answerFilter.clear')}
          </Button>
        )}
      </Flex>
      {question.options.map((option) => {
        const on = picked.includes(option.id);
        const share = question.answered ? Math.round((option.count / question.answered) * 100) : 0;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(option.id)}
            className={`mb-1 flex w-full cursor-pointer items-center gap-2.5 rounded-[var(--radius-3)] border-0 px-2.5 py-2 text-left ${
              on ? 'bg-[var(--violet-3)]' : 'bg-transparent hover:bg-[var(--gray-a3)]'
            }`}
          >
            <Check
              size={15}
              strokeWidth={2.5}
              className={`flex-none text-[var(--violet-9)] ${on ? '' : 'invisible'}`}
            />
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <Text size="2" weight={on ? 'medium' : 'regular'}>
                {option.label}
              </Text>
              <span className="h-1 overflow-hidden rounded-full bg-[var(--gray-a3)]">
                <span className="block h-full rounded-full bg-[var(--violet-6)]" style={{ width: `${share}%` }} />
              </span>
            </span>
            <Text size="1" tone="muted" className="tabular-nums">
              {option.count}
            </Text>
          </button>
        );
      })}
    </>
  );
};

const Heading = ({ children, hint, divider }: { children: string; hint?: string; divider?: boolean }) => (
  <Text
    as="div"
    size="1"
    weight="bold"
    title={hint}
    className={`px-3 pb-1 tracking-wider uppercase ${divider ? 'mt-2 border-t border-[var(--gray-a4)] pt-3' : 'pt-3'}`}
  >
    {children}
  </Text>
);

const Hint = ({ children }: { children: string }) => (
  <Text as="p" size="2" tone="muted" className="px-3 py-4">
    {children}
  </Text>
);

export default AnswerFilterPanel;
